/**
 * Cross-section event bus using BroadcastChannel.
 *
 * Allows micro-frontend sections to communicate state changes
 * without sharing Apollo caches or React context trees.
 *
 * Usage:
 *   // In the section that produces the event (e.g. hub):
 *   import { emitSectionEvent, SECTION_EVENTS } from "@hydrologiq/shared/hooks/useSectionEvent"
 *   emitSectionEvent(SECTION_EVENTS.CONNECTOR_CREATED)
 *
 *   // In the section that consumes the event (e.g. systems):
 *   import { useSectionEvent, SECTION_EVENTS } from "@hydrologiq/shared/hooks/useSectionEvent"
 *   useSectionEvent(SECTION_EVENTS.CONNECTOR_CREATED, () => refetchAvailableConnections())
 */
import { useEffect } from "react"

const CHANNEL_NAME = "hydrologiq:section-events"

export const SECTION_EVENTS = {
  CONNECTOR_CREATED: "connector:created",
  CONNECTOR_DELETED: "connector:deleted",
  CONNECTOR_UPDATED: "connector:updated",
  ASSET_CREATED: "asset:created",
  ASSET_DELETED: "asset:deleted",
  ASSET_ASSIGNED: "asset:assigned",
  ASSET_UNASSIGNED: "asset:unassigned",
  ASSET_MAPPED: "asset:mapped",
  MONITORING_STATUS_CHANGED: "monitoring:status-changed",
} as const

export type SectionEventType = (typeof SECTION_EVENTS)[keyof typeof SECTION_EVENTS]

interface SectionEvent {
  type: SectionEventType
  timestamp: number
}

/**
 * Emit an event to all other open sections via BroadcastChannel.
 * Safe to call from non-React code (e.g. mutation callbacks).
 */
export function emitSectionEvent(type: SectionEventType): void {
  try {
    const channel = new BroadcastChannel(CHANNEL_NAME)
    const event: SectionEvent = { type, timestamp: Date.now() }
    channel.postMessage(event)
    channel.close()
  } catch {
    // BroadcastChannel not supported (e.g. SSR or very old browsers) — silently ignore
  }
}

/**
 * React hook that listens for a specific section event and invokes a callback.
 * Cleans up the channel subscription on unmount.
 */
export function useSectionEvent(
  type: SectionEventType,
  callback: () => void
): void {
  useEffect(() => {
    let channel: BroadcastChannel
    try {
      channel = new BroadcastChannel(CHANNEL_NAME)
    } catch {
      return
    }

    const handler = (event: MessageEvent<SectionEvent>) => {
      if (event.data?.type === type) {
        callback()
      }
    }

    channel.addEventListener("message", handler)
    return () => {
      channel.removeEventListener("message", handler)
      channel.close()
    }
  }, [type, callback])
}
